<template>
  <div
    :class="[
      'chat',
      { seller: user?.is_admin },
      { 'is-reply': user?.is_admin && state.forUser?.id },
      { 'pinned-message': state.pinMessage?.id }
    ]">
    <custom-scrollbar
      class="custom-scrollbar messages-wrapper controller-scrollbar"
      :class="{ 'no-auth': !user?.id }"
      @scrollerComponent="onInitScroll">
      <Preloader v-if="state.chatMessagesPreloaderShow" class="chat"></Preloader>
      <div v-if="state.chatMessagesInfiniteLoaderEnabled" class="observe" ref="observe"></div>
      <div v-if="state.pinMessage?.id" class="pin-message-wrapper">
        <div class="pin-message">
          <base-icon
            class="keep"
            icon-name="keep"
            width="20"
            height="20"
            view-box="0 -960 960 960"
            @click="onPinMessage(state.pinMessage)">
            <keep-icon></keep-icon>
          </base-icon>
          <div class="chat-message" v-html="state.pinMessage.text"></div>
        </div>
      </div>
      <transition-group name="messages">
        <div
          v-for="message in reversedMessages"
          :key="message.id"
          :class="[
            'chat-message-wrapper',
            {
              'auctioneer-message': message.direction === MessageDirections.DIRECTION_TO_USER,
              'reply-message': user?.is_admin && message.user?.id
            }
          ]">
          <base-icon
            :class="['keep', { pinned: message.is_pinned }]"
            v-if="user?.is_admin && message.direction === MessageDirections.DIRECTION_TO_USER && !message.user?.id"
            icon-name="keep"
            width="20"
            height="20"
            view-box="0 -960 960 960"
            @click="onPinMessage(message)">
            <keep-icon></keep-icon>
          </base-icon>
          <div
            class="chat-message"
            :class="{
              reply: user?.is_admin && message.user?.id,
              highlighted: state.blinkMessages.includes(message.id)
            }">
            <div
              class="reply-to"
              v-if="user?.is_admin && message.user?.id && message.direction === MessageDirections.DIRECTION_TO_USER">
              <div class="recipient-name">
                {{ trans("live.common.chat.answer_to", { user: message.user.full_name }) }}
              </div>
            </div>
            <div
              class="reply-to"
              v-if="!user?.is_admin && message.user?.id && message.direction === MessageDirections.DIRECTION_TO_USER">
              <div class="recipient-name">
                {{ trans("live.common.chat.private_message") }}
              </div>
            </div>
            <div v-html="message.text"></div>
            <base-icon
              class="icon-reply"
              v-if="user?.is_admin && message.direction === MessageDirections.DIRECTION_TO_ADMIN"
              icon-name="reply"
              width="18"
              height="15"
              view-box="0 0 18 15"
              @click="answerFor(message)">
              <reply-icon></reply-icon>
            </base-icon>
            <p class="chat-time">
              <span>{{ message.createdAtHumanized }}</span>
            </p>
          </div>
          <div class="chat-sender-name">
            {{ message.sender }}
            <template v-if="user?.is_admin && message.direction === MessageDirections.DIRECTION_TO_ADMIN">
              <base-icon
                class="icon-microphone-off"
                v-if="state.mutedUsers.includes(message.user.id)"
                icon-name="microphone-off"
                width="15.158"
                height="16"
                view-box="0 0 15.158 16"
                @click="unMuteUser(message.user)">
                <microphone-off-icon></microphone-off-icon>
              </base-icon>
              <base-icon
                class="icon-microphone"
                icon-name="microphone"
                v-else
                width="11.79"
                height="16"
                view-box="0 0 11.79 16"
                @click="muteUser(message.user)">
                <microphone-icon></microphone-icon>
              </base-icon>
            </template>
          </div>
        </div>
      </transition-group>
    </custom-scrollbar>

    <div v-if="user?.id && !user.customer_is_muted" class="chat-input-wrapper">
      <label v-if="user.is_admin && state.forUser?.id" class="to-reply-message">
        <span class="to-reply__user-name">
          {{ trans("live.common.chat.answer_to", { user: state.forUser?.full_name }) }}
        </span>
        <span v-if="state.forUser.message" class="to-reply__user-message">{{ state.forUser.message.text }}</span>
        <span class="close-place">
          <base-icon
            class="icon-close"
            icon-name="close"
            width="12"
            height="12"
            viewBox="0 0 26.87 26.87"
            @click="cancelAnswer">
            <close-icon></close-icon>
          </base-icon>
        </span>
      </label>
      <input
        class="chat-input-field"
        v-model="state.message"
        type="text"
        :placeholder="inputPlaceholder"
        required
        @keyup.stop="onKey"
        @keydown.stop
        ref="messageInput" />
      <div class="btn-send-message">
        <base-icon icon-name="send" @click="send">
          <send-icon></send-icon>
        </base-icon>
      </div>
    </div>
    <div v-else-if="user?.id && user.customer_is_muted" class="chat-input-wrapper is-muted">
      {{ trans("live.common.chat.messaging_disabled") }}
    </div>
  </div>
</template>
<script setup lang="ts">
  import simplebar from "simplebar-vue";
  import moment from "moment-timezone";
  import apiClient from "@/services/api";
  import CustomScrollbar from "@/components/_partials/CustomScrollbar.vue";
  import {
    BaseIcon,
    CloseIcon,
    KeepIcon,
    MicrophoneIcon,
    MicrophoneOffIcon,
    ReplyIcon,
    SendIcon
  } from "@/components/icons";
  import { computed, inject, nextTick, onMounted, reactive, ref, watch, watchEffect } from "vue";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { IMessage, IMessageData, IUser } from "@/models";
  import { MessageDirections } from "@/enum";
  import Preloader from "@/components/_partials/Preloader.vue";
  import { useToast } from "primevue/usetoast";
  import { trans } from "@/utils/trans";

  const auctionStore = useAuctionStore();
  const user = computed(() => auctionStore.user);
  const observe = ref<HTMLDivElement>();
  const container = ref<typeof simplebar>();
  const messageInput = ref<HTMLInputElement>();
  const toast = useToast();
  const emitter = inject("emitter");

  const props = withDefaults(
    defineProps<{
      isVisible?: boolean;
    }>(),
    {
      isVisible: false
    }
  );

  const state = reactive({
    format: {
      date: "DD MMM YYYY",
      time: "HH:mm",
      dbDateFormat: "YYYY-MM-DDTHH:mm:ss.SSSSZ"
    },
    message: null as null | string,
    forUser: null as null | IUser,
    awaitFormServerResponse: false,
    chatMessages: [] as IMessage[],
    chatMessagesInfiniteLoaderEnabled: false,
    chatMessagesPreloaderShow: false,
    chatMessagesLastLoadedDate: null as null | string,
    chatMessagesLastLoadedId: null as null | number,
    chatMessagesIntersectionObserver: null as null | IntersectionObserver,
    pinMessage: null as null | IMessage,
    blinkMessages: [] as number[],
    clearBlinkMessagesTimerId: null as null | number,
    mutedUsers: [] as number[],
    awaitChangeMuteState: false
  });

  watchEffect(() => {
    if (observe.value && state.chatMessagesIntersectionObserver) {
      if (props.isVisible) {
        state.chatMessagesIntersectionObserver.observe(observe.value);
      } else {
        state.chatMessagesIntersectionObserver.unobserve(observe.value);
      }
    }
  });

  watch(
    () => props.isVisible,
    (nv) => {
      if (nv) {
        scrollToBottom();
      }
    }
  );

  watchEffect(() => {
    if (state.blinkMessages.length && auctionStore.pageIsActive && props.isVisible) {
      if (state.clearBlinkMessagesTimerId) {
        clearTimeout(state.clearBlinkMessagesTimerId);
      }
      state.clearBlinkMessagesTimerId = setTimeout(() => (state.blinkMessages = []), 10000);
    }
    if (auctionStore.hasNewChatMessage && props.isVisible && auctionStore.pageIsActive) {
      markRead();
    }
  });

  const reversedMessages = computed(() => {
    return [...(state.chatMessages || [])].reverse();
  });
  const inputPlaceholder = computed(() => {
    if (auctionStore.user?.is_admin) {
      return trans("live.common.chat.admin_message_placeholder");
    }
    return trans("live.common.chat.client_message_placeholder");
  });

  onMounted(async () => {
    state.chatMessagesLastLoadedDate = getNow();
    getMutedUsers();
    loadChatMessages(true, false);

    state.chatMessagesIntersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio <= 0) return;

      if (state.chatMessagesInfiniteLoaderEnabled) {
        loadChatMessages(false, true);
      }
    });

    emitter?.on("on:ws-chat-new-message", handleNewMessage);
    emitter?.on("on:ws-chat-new-admin-message", handleNewAdminMessage);
    emitter?.on("on:ws-chat-pin-message", handlePinMessage);
  });

  const handleNewMessage = (data: { userId?: number; message: IMessageData }) => {
    state.chatMessages = [getMessageFromMessageData(data.message), ...state.chatMessages];
    hasNewMessage(data.message);
    scrollToBottom();
  };

  const handleNewAdminMessage = (data: { userId?: number; message: IMessageData }) => {
    const newMessage = getMessageFromMessageData(data.message);
    state.chatMessages = [newMessage, ...state.chatMessages];
    if (newMessage.direction === MessageDirections.DIRECTION_TO_USER && !newMessage.user?.id) {
      emitter?.emit("on:chat-new-message", newMessage);
    }
    hasNewMessage(data.message);
    scrollToBottom();
  };

  const handlePinMessage = (data: { message: IMessageData | null }) => {
    state.pinMessage = data.message ? getMessageFromMessageData(data.message) : null;
    auctionStore.$patch({
      pinMessage: state.pinMessage
    });
    if (data.message) {
      hasNewMessage(data.message);
    }
  };

  const onInitScroll = (scrollbar: typeof simplebar) => {
    container.value = scrollbar.value.scrollElement;
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      send();
    }
  };

  const answerFor = (message: IMessage) => {
    state.forUser = message.user;
    state.forUser.message = message;
    messageInput.value?.focus();
  };

  const cancelAnswer = () => {
    state.forUser = null;
  };

  const loadChatMessages = (clear = false, loadMore = false, fromDate = "") => {
    if (!auctionStore.auction?.id) {
      return;
    }
    state.chatMessagesInfiniteLoaderEnabled = false;

    if (clear) {
      state.chatMessagesPreloaderShow = true;
      state.chatMessages = [];
    }

    let filter = {
      auction_id: auctionStore.auction.id,
      to_date: getNow(),
      limit: 50
    } as {
      auction_id: number;
      to_date?: string;
      from_date?: string;
      limit: number;
      not_id?: number | null;
    };

    if (loadMore) {
      state.chatMessagesPreloaderShow = true;

      if (state.chatMessagesLastLoadedDate) {
        filter.to_date = moment.utc(state.chatMessagesLastLoadedDate).format(state.format.dbDateFormat);
      }
      if (state.chatMessagesLastLoadedId) {
        filter.not_id = state.chatMessagesLastLoadedId;
      }
    }

    if (fromDate && fromDate.length > 0) {
      filter.from_date = moment.utc(fromDate).format(state.format.dbDateFormat);
      filter.limit = 1000;
    }

    apiClient.get(auctionStore.routes["live_chat.get"] + "?" + new URLSearchParams(filter)).then((response) => {
      const { messages, total, pin_message } = response.data;

      let prevContainerHeight = container.value?.scrollHeight;

      state.chatMessagesPreloaderShow = false;
      state.chatMessagesInfiniteLoaderEnabled = filter.limit < total;

      state.pinMessage = pin_message ? getMessageFromMessageData(pin_message) : null;

      auctionStore.$patch({
        pinMessage: state.pinMessage
      });

      messages.forEach((messageData: IMessageData) => {
        const message = getMessageFromMessageData(messageData);

        if (!messageData.is_read) {
          if (
            (user.value?.is_admin && messageData.direction == MessageDirections.DIRECTION_TO_ADMIN) ||
            (!user.value?.is_admin &&
              messageData.user_id &&
              messageData.direction == MessageDirections.DIRECTION_TO_USER)
          ) {
            auctionStore.$patch({
              hasNewChatMessage: true
            });
          }
        }

        if (moment.utc(message.createdAt).isBefore(moment.utc(state.chatMessagesLastLoadedDate))) {
          state.chatMessagesLastLoadedDate = moment(message.createdAt).format();
          state.chatMessagesLastLoadedId = messageData.id;
        }

        state.chatMessages.push(message);
      });

      nextTick(() => {
        if (container.value) {
          if (loadMore) {
            container.value.scrollTop = container.value.scrollHeight - prevContainerHeight;
          } else {
            scrollToBottom();
          }
        }
      });
    });
  };

  const getNow = () => {
    return moment.utc().format(state.format.dbDateFormat);
  };

  const scrollToBottom = () => {
    nextTick(() => {
      if (container.value) {
        container.value.scrollTop = container.value.scrollHeight;
      }
    });
  };

  const getMessageFromMessageData = (messageData: IMessageData): IMessage => {
    return {
      id: messageData.id,
      direction: messageData.direction,
      sender: messageData.direction === 2 ? messageData.sender || `Oldlouis Auctions` : messageData.user?.full_name,
      text: messageData.message,
      fileType: messageData.file?.file_type,
      filePath: messageData.file?.file_path,
      fileName: messageData.file?.file_name,
      is_pinned: messageData.is_pinned,
      createdAt: messageData.created_at,
      createdAtHumanized: moment(messageData.created_at).format(state.format.time),
      user: messageData.user
    };
  };

  const send = () => {
    if (state.awaitFormServerResponse || !user.value?.id || !auctionStore.auction?.id) {
      return;
    }

    if (!state.message?.length) {
      return;
    }

    if (state.forUser && !state.forUser.id) {
      toast.add({
        severity: "error",
        summary: trans("live.toast.error"),
        detail: trans("live.toast.unknown_error"),
        life: 6000
      });
    }

    const message = state.message;
    const userId = state.forUser?.id;
    state.message = null;
    state.forUser = null;
    state.awaitFormServerResponse = true;

    apiClient
      .post(auctionStore.routes["live_chat.store"], {
        auction_id: auctionStore.auction.id,
        user_id: userId,
        message
      })
      .then((response) => {
        if (!response.data.status) {
          toast.add({
            severity: "error",
            summary: trans("live.toast.error"),
            detail: response.data.message || trans("live.toast.unknown_error"),
            life: 6000
          });
        }
      })
      .catch(() => {
        state.message = message;
      })
      .finally(() => (state.awaitFormServerResponse = false));
  };

  const hasNewMessage = (message: IMessageData) => {
    if (
      (user.value?.is_admin && message.direction == MessageDirections.DIRECTION_TO_ADMIN) ||
      (!user.value?.is_admin && message.direction == MessageDirections.DIRECTION_TO_USER)
    ) {
      if (!auctionStore.pageIsActive || !props.isVisible) {
        const newMessages = state.blinkMessages;
        newMessages.push(message.id);
        state.blinkMessages = [];
        nextTick(() => {
          state.blinkMessages = newMessages;
        });
      }
      auctionStore.hasNewChatMessage = true;
    }
  };
  const onPinMessage = (message: IMessage) => {
    if (!user.value?.id || !user.value?.is_admin || !auctionStore.auction?.id) {
      return;
    }

    apiClient
      .post(auctionStore.routes["live_chat.pin"], {
        auction_id: auctionStore.auction.id,
        message_id: message.id
      })
      .then(() => {
        state.chatMessages.forEach((item) => {
          item.is_pinned = false;
        });
        message.is_pinned = true;
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => (state.awaitFormServerResponse = false));
  };

  const markRead = () => {
    if (!user.value?.id || !auctionStore.auction?.id) {
      return;
    }

    apiClient
      .put(auctionStore.routes["live.chat.mark_read"], {
        auctionId: auctionStore.auction.id
      })
      .then(() => {
        state.chatMessages.forEach((message) => {
          message.is_read = true;
        });

        auctionStore.$patch({
          hasNewChatMessage: false
        });
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const getMutedUsers = () => {
    if (!user.value?.is_admin) {
      return;
    }

    apiClient
      .get(auctionStore.routes["live.users.get_muted_users"])
      .then((response) => {
        state.mutedUsers = response.data;
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const muteUser = (user: IUser) => {
    updateUserMute(user, auctionStore.routes["live.users.mute"]);
  };
  const unMuteUser = (user: IUser) => {
    updateUserMute(user, auctionStore.routes["live.users.unmute"]);
  };

  const updateUserMute = (user: IUser, href: string) => {
    if (state.awaitChangeMuteState) {
      return;
    }

    state.awaitChangeMuteState = true;
    apiClient
      .put(href, { userId: user.id })
      .then((response) => {
        if (response.data.status) {
          if (response.data.customer_is_muted) {
            state.mutedUsers.push(user.id);
          } else {
            state.mutedUsers = state.mutedUsers.filter((id: number) => {
              return id !== user.id;
            });
          }

          auctionStore.$patch((storeState) => {
            if (storeState.userData[user.id]) {
              storeState.userData[user.id].customer_is_muted = response.data.customer_is_muted;
            }
            const index = auctionStore.activeLot.bidders.findIndex((item) => {
              return item.id === user.id;
            });
            if (index >= 0) {
              storeState.activeLot.bidders[index].customer_is_muted = response.data.customer_is_muted;
            }
          });
        }
        toast.add({
          severity: response.data.status ? "success" : "error",
          summary: trans("live.toast." + (response.data.status ? "success" : "error")),
          detail: response.data.message || trans("live.toast.unknown_error"),
          life: 6000
        });
      })
      .finally(() => (state.awaitChangeMuteState = false));
  };

  emitter.on("on:user-message", (data: IUser) => {
    state.forUser = {
      id: data.id,
      full_name: data.full_name
    } as IUser;
  });

  emitter.on("on:user-mute", (data: { userId: number; customerIsMuted: boolean }) => {
    if (data.customerIsMuted) {
      state.mutedUsers.push(data.userId);
    } else {
      state.mutedUsers = state.mutedUsers.filter((id: number) => {
        return id !== data.userId;
      });
    }
  });
</script>

<style>
  .messages-enter-active,
  .messages-leave-active {
    transition: all 0.5s ease;
  }
  .messages-enter-from,
  .messages-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }
</style>
